import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Box,
} from '@mui/material';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';

/** Suggest a funding call to a lab. The suggestion reaches the lab's PI. */
export default function LabSuggestCallDialog({ open, onClose, lab }) {
  const { showSnackbar } = useSnackbar();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [call, setCall] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await axiosService.get(`/funding/calls/?search=${encodeURIComponent(query)}&page_size=10`);
        setOptions(res.data?.results || res.data || []);
      } catch { setOptions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  const close = (e) => {
    e?.stopPropagation?.();
    setCall(null); setNote(''); setQuery('');
    onClose();
  };

  const send = async () => {
    if (!call) return;
    setSending(true);
    try {
      await axiosService.post(`/interact/suggestions/by_target/call/${call.id}/`, {
        recipient: lab.pi,
        target_type: 'call',
        target_id: call.id,
        kind: 'collaboration',
        body: note.trim() || `A funding call that may suit ${lab.name}: ${call.title}`,
        metadata: { lab_id: lab.id, lab_name: lab.name },
        status: 'open',
      });
      showSnackbar(`Suggested to ${lab.name} (${lab.pi_detail?.name || 'the PI'} will see it)`, 'success');
      close();
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'Could not send the suggestion.'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle>Suggest a funding call to {lab?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The lab&apos;s PI, {lab?.pi_detail?.name}, receives the suggestion.
        </Typography>
        <Autocomplete
          options={options}
          value={call}
          onChange={(_, v) => setCall(v)}
          onInputChange={(_, v) => setQuery(v)}
          filterOptions={(x) => x}
          getOptionLabel={(o) => o?.title || ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.id}>
              <Box>
                <Typography variant="body2">{o.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[o.identifier || o.call_id, o.deadline && `deadline ${String(o.deadline).slice(0, 10)}`].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Funding call" placeholder="Search calls…" />}
        />
        <TextField
          fullWidth multiline minRows={3} sx={{ mt: 2 }}
          label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Why this call fits the lab…"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={!call || sending} onClick={send}>Suggest</Button>
      </DialogActions>
    </Dialog>
  );
}
